import { TAGS } from "../constants";
import { IntakeQuestion, UpdateIntakeQuestion } from "../types";
import { api } from "./api";

export const intakeQuestionsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getIntakeQuestions: builder.query<IntakeQuestion[], number>({
      query: (intakeId) => ({
        method: "GET",
        url: `/api/intakes/${intakeId}/questions`,
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.INTAKE_QUESTIONS, id: args },
      ],
    }),
    getIntakeQuestion: builder.query<
      IntakeQuestion,
      { intakeId: number; questionPosition: number }
    >({
      query: ({ intakeId, questionPosition }) => ({
        method: "GET",
        url: `/api/intakes/${intakeId}/questions/position/${questionPosition}`,
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.INTAKE_QUESTION, id: args.intakeId },
      ],
    }),
    createIntakeQuestion: builder.mutation<
      IntakeQuestion,
      {
        intakeId: number;
        body: {
          question_id: number;
          question_position: number;
          next_question?: string;
        };
      }
    >({
      query: ({ intakeId, body }) => ({
        method: "POST",
        url: `/api/intakes/${intakeId}/questions`,
        body,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        dispatch(
          intakeQuestionsApi.util.updateQueryData(
            "getIntakeQuestions",
            args.intakeId,
            (draft) => {
              draft.push(data);
            },
          ),
        );
      },
    }),
    updateIntakeQuestion: builder.mutation<
      IntakeQuestion,
      { intakeId: number; questionId: number; body: UpdateIntakeQuestion }
    >({
      query: ({ intakeId, questionId, body }) => ({
        method: "PUT",
        url: `/api/intakes/${intakeId}/questions/${questionId}`,
        body,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          intakeQuestionsApi.util.updateQueryData(
            "getIntakeQuestions",
            args.intakeId,
            (draft) => {
              const question = draft.find((q) => q.id === args.questionId);

              if (question == null) return;

              if (args.body.next_question != null) {
                question.next_question!.slug = args.body.next_question;
              }
              if (args.body.question_position != null) {
                question.question_position = args.body.question_position;
              }
            },
          ),
        );

        queryFulfilled.catch(patch.undo);
      },
    }),
    bulkUpdateQuestions: builder.mutation<
      IntakeQuestion[],
      {
        intakeId: number;
        body: UpdateIntakeQuestion[];
      }
    >({
      query: ({ body, intakeId }) => ({
        method: "PUT",
        url: `/api/intakes/${intakeId}/questions/bulk-update`,
        body,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          intakeQuestionsApi.util.updateQueryData(
            "getIntakeQuestions",
            args.intakeId,
            (draft) => {
              args.body.forEach(({ question_id, question_position }) => {
                const question = draft.find(
                  (q) => q.question_id === question_id,
                );
                if (question != null && question_position != null) {
                  question.question_position = question_position;
                }
              });
            },
          ),
        );

        queryFulfilled.catch(patch.undo);
      },
    }),
    copyQuestions: builder.mutation<
      { message: string },
      {
        selectedIds: number[];
        extractedIntakeId: number;
        receivingIntakeId: number;
      }
    >({
      query: ({ extractedIntakeId, selectedIds, receivingIntakeId }) => ({
        method: "POST",
        url: `/api/intakes/${extractedIntakeId}/questions/copy-items`,
        body: {
          selectedIds,
          receivingIntakeId,
        },
      }),
      invalidatesTags: (_results, _error, args) => [
        {
          type: TAGS.INTAKE_QUESTIONS,
          id: args.receivingIntakeId,
        },
      ],
    }),
    deleteQuestions: builder.mutation<
      IntakeQuestion[],
      { selectedIds: number[] }
    >({
      query: (body) => ({
        method: "DELETE",
        url: `/api/intakes`,
        body,
      }),
      invalidatesTags: [TAGS.INTAKE_QUESTIONS],
    }),
  }),
});

export const {
  useGetIntakeQuestionQuery,
  useGetIntakeQuestionsQuery,
  useUpdateIntakeQuestionMutation,
  useCreateIntakeQuestionMutation,
  useBulkUpdateQuestionsMutation,
  useCopyQuestionsMutation,
  useDeleteQuestionsMutation,
} = intakeQuestionsApi;
